import { sendPaymentReminder } from './emailService';

const today = () => new Date().toISOString().split('T')[0];

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const formatEuro = (v) =>
  Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

/**
 * Esegue i tool di Olli contro lo stato reale dell'app.
 * Riceve le funzioni CRUD direttamente da useAppState().
 */
export class OlliToolExecutor {
  constructor(appState) {
    this.appState = appState;
  }

  async execute(toolName, toolInput) {
    const handler = this[toolName];
    if (!handler) {
      return { success: false, error: `Tool sconosciuto: ${toolName}` };
    }
    try {
      return await handler.call(this, toolInput);
    } catch (err) {
      console.error(`Olli tool error [${toolName}]:`, err);
      return { success: false, error: err.message || 'Errore nell\'esecuzione dell\'azione.' };
    }
  }

  // ── create_invoice ──────────────────────────────────────────
  async create_invoice({ clientName, grossAmount, issueDate, dueDate, notes }) {
    const { createInvoice, getNextSeqForYear, getInvoiceCodeForYear } = this.appState;

    const date = issueDate || today();
    const year = new Date(date).getFullYear();
    const seq = getNextSeqForYear(year);
    const invoiceCode = getInvoiceCodeForYear(year);

    const id = await createInvoice({
      issueDate: date,
      dueDate: dueDate || addDays(date, 15),
      clientName,
      grossAmount,
      notes: notes || '',
      seq,
      invoiceCode,
      paid: false,
    });

    return {
      success: true,
      message: `Fattura ${invoiceCode} creata per ${clientName} — ${formatEuro(grossAmount)}`,
      data: { id, invoiceCode, clientName, grossAmount, issueDate: date },
    };
  }

  // ── mark_invoice_paid ───────────────────────────────────────
  async mark_invoice_paid({ identifier, paymentDate }) {
    const { invoices, updateInvoice } = this.appState;
    const found = this._findInvoice(invoices, identifier);
    if (!found) {
      return { success: false, error: `Nessuna fattura trovata per "${identifier}"` };
    }
    if (found.paid) {
      return { success: false, error: `La fattura ${found.invoiceCode} è già incassata.` };
    }

    await updateInvoice(found.id, {
      paid: true,
      paymentDate: paymentDate || today(),
    });

    return {
      success: true,
      message: `Fattura ${found.invoiceCode} (${found.clientName}) segnata come incassata.`,
      data: { id: found.id, invoiceCode: found.invoiceCode },
    };
  }

  // ── search_invoices ─────────────────────────────────────────
  async search_invoices({ clientName, paid, year, month, paymentYear, paymentMonth }) {
    const { invoices } = this.appState;
    let results = [...invoices];

    if (clientName) {
      const q = clientName.toLowerCase();
      results = results.filter((inv) => (inv.clientName || '').toLowerCase().includes(q));
    }
    if (paid !== undefined) {
      results = results.filter((inv) => inv.paid === paid);
    }
    // Filtri per data di EMISSIONE (fatturato / competenza).
    if (year) {
      results = results.filter(
        (inv) => inv.issueDate && new Date(inv.issueDate).getFullYear() === year
      );
    }
    if (month) {
      results = results.filter(
        (inv) => inv.issueDate && new Date(inv.issueDate).getMonth() + 1 === month
      );
    }
    // Filtri per data di INCASSO (incassato / principio di cassa): solo fatture pagate,
    // per paymentDate con fallback a issueDate se pagata senza data di pagamento.
    if (paymentYear) {
      results = results.filter((inv) => {
        if (!inv.paid) return false;
        const d = inv.paymentDate || inv.issueDate;
        return d && new Date(d).getFullYear() === paymentYear;
      });
    }
    if (paymentMonth) {
      results = results.filter((inv) => {
        if (!inv.paid) return false;
        const d = inv.paymentDate || inv.issueDate;
        return d && new Date(d).getMonth() + 1 === paymentMonth;
      });
    }

    // Payload compatto ma con paymentDate (necessario per ragionare sull'incassato
    // per cassa). `totale` = somma dei grossAmount dei risultati, pronta all'uso.
    const summary = results.slice(0, 20).map((inv) => ({
      id: inv.id,
      invoiceCode: inv.invoiceCode,
      clientName: inv.clientName,
      grossAmount: inv.grossAmount,
      issueDate: inv.issueDate,
      paymentDate: inv.paymentDate || null,
      paid: inv.paid,
    }));

    const totale = results.reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

    return {
      success: true,
      message: `Trovate ${results.length} fatture.`,
      data: { count: results.length, totale, invoices: summary },
    };
  }

  // ── update_invoice ──────────────────────────────────────────
  async update_invoice({ identifier, updates }) {
    const { invoices, updateInvoice } = this.appState;
    const found = this._findInvoice(invoices, identifier);
    if (!found) {
      return { success: false, error: `Nessuna fattura trovata per "${identifier}"` };
    }

    await updateInvoice(found.id, updates);

    return {
      success: true,
      message: `Fattura ${found.invoiceCode} aggiornata.`,
      data: { id: found.id, invoiceCode: found.invoiceCode, updates },
    };
  }

  // ── delete_invoices ─────────────────────────────────────────
  async delete_invoices({ invoiceIds, confirmed }) {
    const { invoices, deleteInvoice } = this.appState;

    if (!confirmed) {
      const toDelete = invoices.filter((inv) => invoiceIds.includes(inv.id));
      const desc = toDelete
        .map((inv) => `${inv.invoiceCode} — ${inv.clientName} (${formatEuro(inv.grossAmount)})`)
        .join('\n');
      return {
        success: true,
        message: `Vuoi davvero eliminare ${toDelete.length} fattura/e?\n${desc}\n\nConferma per procedere.`,
        data: { needsConfirmation: true, invoiceIds },
      };
    }

    for (const id of invoiceIds) {
      await deleteInvoice(id);
    }

    return {
      success: true,
      message: `${invoiceIds.length} fattura/e eliminata/e.`,
      data: { deleted: invoiceIds.length },
    };
  }

  // ── export_invoices_csv ─────────────────────────────────────
  async export_invoices_csv({ year, paid }) {
    const { invoices, selectedYear } = this.appState;
    const targetYear = year || selectedYear;

    let filtered = invoices.filter(
      (inv) => inv.issueDate && new Date(inv.issueDate).getFullYear() === targetYear
    );
    if (paid !== undefined) {
      filtered = filtered.filter((inv) => inv.paid === paid);
    }

    if (filtered.length === 0) {
      return { success: false, error: `Nessuna fattura trovata per l'anno ${targetYear}.` };
    }

    const BOM = '\uFEFF';
    const header = 'Numero;Data Emissione;Cliente;Importo Lordo;Incassata;Data Incasso';
    const rows = filtered.map((inv) =>
      [
        inv.invoiceCode,
        inv.issueDate,
        inv.clientName,
        String(inv.grossAmount).replace('.', ','),
        inv.paid ? 'Sì' : 'No',
        inv.paymentDate || '',
      ].join(';')
    );

    const csv = BOM + header + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    return {
      success: true,
      message: `Export CSV pronto con ${filtered.length} fatture.`,
      data: {
        downloadUrl: url,
        fileName: `fatture_${targetYear}.csv`,
        count: filtered.length,
      },
    };
  }

  // ── create_client ───────────────────────────────────────────
  async create_client({ name, email, pecEmail, fiscalCode, vatNumber, sdiCode, phone, address, compensationType, rate, paymentTermsDays, notes }) {
    const { createClient, clients } = this.appState;

    // Controlla duplicato
    const existing = clients.find(
      (c) => c.name && c.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      return { success: false, error: `Il cliente "${name}" esiste già.` };
    }

    const id = await createClient({
      name,
      email: email || '',
      pecEmail: pecEmail || '',
      fiscalCode: fiscalCode || '',
      vatNumber: vatNumber || '',
      sdiCode: sdiCode || '',
      phone: phone || '',
      address: address || '',
      compensationType: compensationType || 'daily',
      rate: rate || 0,
      paymentTermsDays: paymentTermsDays || 30,
      notes: notes || '',
    });

    return {
      success: true,
      message: `Cliente "${name}" creato con successo.`,
      data: { id, name },
    };
  }

  // ── log_timesheet_entry ─────────────────────────────────────
  async log_timesheet_entry({ clientId, clientName, year, month, day, value, task }) {
    const { timesheets, clients, createTimesheet, updateTimesheet } = this.appState;

    // Risolvi il cliente
    let resolvedClientId = clientId;
    let resolvedClientName = clientName;
    if (!resolvedClientId && clientName) {
      const client = clients.find(
        (c) => c.name && c.name.toLowerCase().includes(clientName.toLowerCase())
      );
      if (client) {
        resolvedClientId = client.id;
        resolvedClientName = client.name;
      }
    }
    if (!resolvedClientId) {
      return { success: false, error: 'Cliente non trovato. Specifica un nome o ID valido.' };
    }

    // Trova timesheet esistente per cliente/anno/mese
    let timesheet = timesheets.find(
      (t) => t.clientId === resolvedClientId && t.year === year && t.month === month
    );

    const entry = { day, value, task: task || '' };

    if (timesheet) {
      // Aggiorna entries esistenti
      const entries = [...(timesheet.entries || [])];
      const existingIdx = entries.findIndex((e) => e.day === day);
      if (existingIdx >= 0) {
        entries[existingIdx] = entry;
      } else {
        entries.push(entry);
      }

      const totalDays = entries.reduce((sum, e) => sum + (e.value || 0), 0);
      await updateTimesheet(timesheet.id, { entries, totalDays });
    } else {
      // Crea nuovo timesheet
      const client = clients.find((c) => c.id === resolvedClientId);
      await createTimesheet({
        clientId: resolvedClientId,
        clientName: resolvedClientName || '',
        year,
        month,
        entries: [entry],
        totalDays: value,
        rate: client?.rate || 0,
        compensationType: client?.compensationType || 'daily',
      });
    }

    return {
      success: true,
      message: `Registrata presenza per ${resolvedClientName} il ${day}/${month}/${year}: ${value === 1 ? 'giornata intera' : value === 0.5 ? 'mezza giornata' : 'assente'}.`,
      data: { clientId: resolvedClientId, year, month, day, value },
    };
  }

  // ── generate_timesheet_invoice ──────────────────────────────
  async generate_timesheet_invoice({ clientId, clientName, year, month }) {
    const { timesheets, clients, createInvoice, getNextSeqForYear, getInvoiceCodeForYear } = this.appState;

    // Risolvi il cliente
    let resolvedClientId = clientId;
    if (!resolvedClientId && clientName) {
      const client = clients.find(
        (c) => c.name && c.name.toLowerCase().includes(clientName.toLowerCase())
      );
      if (client) resolvedClientId = client.id;
    }

    const timesheet = timesheets.find(
      (t) => t.clientId === resolvedClientId && t.year === year && t.month === month
    );
    if (!timesheet) {
      return { success: false, error: `Nessun timesheet trovato per ${clientName || clientId} — ${month}/${year}` };
    }

    const client = clients.find((c) => c.id === resolvedClientId);
    const totalDays = timesheet.totalDays || 0;
    const rate = timesheet.rate || client?.rate || 0;
    const grossAmount = totalDays * rate;

    if (grossAmount <= 0) {
      return { success: false, error: 'Importo fattura pari a zero. Verifica il timesheet e la tariffa del cliente.' };
    }

    const issueDate = today();
    const invoiceYear = new Date(issueDate).getFullYear();
    const seq = getNextSeqForYear(invoiceYear);
    const invoiceCode = getInvoiceCodeForYear(invoiceYear);

    const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

    const id = await createInvoice({
      issueDate,
      clientName: client?.name || timesheet.clientName,
      grossAmount,
      seq,
      invoiceCode,
      notes: `Timesheet ${MONTH_NAMES[month - 1]} ${year} — ${totalDays} giorni × ${formatEuro(rate)}`,
      paid: false,
    });

    return {
      success: true,
      message: `Fattura ${invoiceCode} generata da timesheet: ${totalDays} giorni × ${formatEuro(rate)} = ${formatEuro(grossAmount)}`,
      data: { id, invoiceCode, grossAmount, totalDays },
    };
  }

  // ── send_payment_reminder ───────────────────────────────────
  async send_payment_reminder({ invoiceIdentifier }) {
    const { invoices, clients, user, userId } = this.appState;

    const invoice = this._findInvoice(invoices, invoiceIdentifier);
    if (!invoice) {
      return { success: false, error: `Nessuna fattura trovata per "${invoiceIdentifier}"` };
    }

    return await sendPaymentReminder({
      invoice,
      invoices,
      clients,
      user,
      userId,
      triggeredBy: 'olli',
    });
  }

  // ── start_csv_import ────────────────────────────────────────
  async start_csv_import() {
    return {
      success: true,
      message: 'Apro la modale di importazione CSV...',
      data: { action: 'open_import_modal' },
    };
  }

  // ── Helpers ─────────────────────────────────────────────────
  _findInvoice(invoices, identifier) {
    if (!identifier) return null;
    const q = identifier.toLowerCase().trim();

    // Match esatto per invoiceCode
    const byCode = invoices.find(
      (inv) => (inv.invoiceCode || '').toLowerCase() === q
    );
    if (byCode) return byCode;

    // Match per ID
    const byId = invoices.find((inv) => inv.id === identifier);
    if (byId) return byId;

    // Match parziale per invoiceCode
    const byPartialCode = invoices.find(
      (inv) => (inv.invoiceCode || '').toLowerCase().includes(q)
    );
    if (byPartialCode) return byPartialCode;

    // Match per nome cliente (prima fattura non pagata)
    const byClient = invoices
      .filter((inv) => (inv.clientName || '').toLowerCase().includes(q))
      .sort((a, b) => (a.paid === b.paid ? 0 : a.paid ? 1 : -1));
    return byClient[0] || null;
  }
}
